import React, { useCallback, useContext, useEffect } from "react";
import {
    Typography,
    Box,
    Dialog,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import { Button } from '../Button';
import { Text } from '../Typography';
import { useDrawer } from '../Drawer';
import { useTranslation } from 'react-i18next';
import { useDataProvider } from "src/mtska-frontend-data-provider";
import { AppContext } from 'src/mtska-frontend-app-scaffolding';

const carDataProviderName = 'cars/car';
const drawerRouteBase = 'cars';

const BookingsCountDialog = ({ open, onClose, dateRange, calendarFilters = {}, onOccupancyRefresh }) => {
    const { t } = useTranslation();
    const { activate } = useDrawer();
    const { userCapabilities } = useContext(AppContext);

    const {
        items: vehicles,
        loadAll,
        loading,
        errorMessage,
    } = useDataProvider('calendars/carcalendarrecord/periodvehicles', false);

    const canViewCar = userCapabilities?.[`${carDataProviderName}.view`];

    const loadVehicles = useCallback(() => {
        if (dateRange[0] == null || dateRange[1] == null) {
            return;
        }

        loadAll({
            bookingStart_at: dateRange[0].format('YYYY-MM-DDTHH:mm:ss'),
            bookingEnd_at: dateRange[1].format('YYYY-MM-DDTHH:mm:ss'),
            ...calendarFilters,
        });
    }, [loadAll, dateRange, calendarFilters]);

    useEffect(() => {
        if (!open || dateRange[0] == null || dateRange[1] == null) {
            return;
        }

        loadVehicles();
    }, [open, loadVehicles, dateRange]);

    const handleClose = (e) => {
        e?.preventDefault?.();
        onClose();
    };

    const handleOpenCarDetail = (carId) => {
        if (!carId || !canViewCar) {
            return;
        }

        activate(`${drawerRouteBase}/detail`, { id: carId }, () => {
            loadVehicles();
            onOccupancyRefresh?.();
        });
    };

    const bookingStartAt = dateRange[0];
    const bookingEndAt = dateRange[1];
    const vehicleRows = Array.isArray(vehicles) ? vehicles : [];

    return (
        <Dialog
            className="dialog dialog-confirm dialogBookingsCount"
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
        >
            <Box className="dialog-backdrop"></Box>
            <Box className="dialog-content">
                <Text level="h3" style={{ fontSize: '18px' }}>
                    {t('Bookings in period: {{bookingStart_at}} - {{bookingEnd_at}}', {
                        bookingStart_at: bookingStartAt?.format('DD/MM/YYYY'),
                        bookingEnd_at: bookingEndAt?.format('DD/MM/YYYY'),
                    })}
                </Text>

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress />
                    </Box>
                )}

                {!loading && errorMessage?.length > 0 && (
                    <Typography color="error" variant="body2" sx={{ mt: 2 }}>
                        {errorMessage}
                    </Typography>
                )}

                {!loading && !errorMessage?.length && vehicleRows.length === 0 && (
                    <Typography variant="body2" sx={{ mt: 2, color: 'var(--app-surface-subdued)' }}>
                        {t('No vehicles bookable in this period')}
                    </Typography>
                )}

                {!loading && !errorMessage?.length && vehicleRows.length > 0 && (
                    <TableContainer sx={{ mt: 2, maxHeight: 360 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>{t('Plate')}</TableCell>
                                    <TableCell>{t('Driver')}</TableCell>
                                    <TableCell align="right" width={56} />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {vehicleRows.map((row) => {
                                    const carId = row.car_id ?? row.carId;
                                    const recordId = row.car_calendar_record_id;
                                    const rowKey = row._id ?? row.id ?? `${row.plate}-${recordId ?? carId ?? 'free'}`;

                                    return (
                                        <TableRow key={rowKey}>
                                            <TableCell>{row.plate ?? '—'}</TableCell>
                                            <TableCell>
                                                {row.driver_name ?? (
                                                    row.car_calendar_record_id ? (
                                                        '—'
                                                    ) : (
                                                        <Typography
                                                            component="span"
                                                            variant="body2"
                                                            sx={{ color: 'var(--app-surface-subdued)' }}
                                                        >
                                                            {t('Available')}
                                                        </Typography>
                                                    )
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                {carId && canViewCar && (
                                                    <Button
                                                        icon="faPen"
                                                        variant="plain"
                                                        title={t('Edit')}
                                                        onClick={() => handleOpenCarDetail(carId)}
                                                    />
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                <Box className="dialog-actions" style={{ paddingTop: '20px' }}>
                    <Button variant="solid" className="dialog-ok" onClick={handleClose}>{t('Close')}</Button>
                </Box>
            </Box>
        </Dialog>
    );
};

export default BookingsCountDialog;
